import { Component, OnInit, TemplateRef, ViewChild, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SplitPaneComponent } from '../../components/split-pane/split-pane.component';
import { SplitPaneVerticalComponent } from '../../components/split-pane-vertical/split-pane-vertical.component';
import { ChatComponent } from '../../components/chat/chat.component';
import { AiOrchestratorResult } from '../../components/chat/chat.component';
import { HistoryComponent } from '../../components/history/history.component';
import { InnerSplitPaneComponent } from '../../components/inner-split-pane/inner-split-pane.component';
import { QuotationRecord, QuotationTemplate, QuotationsService } from '../../services/quotations.service';
import { HistoryFacade, DocsFacade, WorkspaceFacade } from '@state';
import { ModalService } from '../../services/modal.service';
import { compactChatMessages, type ChatMessage } from '../../models/orchestrator-surface.model';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

type PricingRow = { label: string; description: string; amount: string };
const CORE_QUOTATION_PLACEHOLDER_ALIASES: Record<string, string[]> = {
  scope_of_work: ['scope-of-work', 'scope.of_work', 'scope'],
  deliverables: ['deliverable', 'deliverable_list'],
  executive_summary: ['executive-summary', 'summary', 'overview'],
  milestones: ['project_milestones', 'delivery_milestones'],
  assumptions: ['project_assumptions', 'delivery_assumptions'],
  exclusions: ['out_of_scope', 'out-of-scope', 'excluded_items'],
  next_steps: ['next-steps', 'acceptance_next_steps'],
  pricing_lines: ['pricing-lines', 'pricing_schedule', 'line_items', 'commercial_lines'],
  total_amount: ['total-amount', 'total.amount', 'amount_total', 'quote_total', 'total'],
  currency: ['currency_code', 'curr'],
  payment_terms: ['payment-terms', 'payment.terms', 'terms'],
};

@Component({
  selector: 'app-quotations',
  templateUrl: './quotations.page.html',
  styleUrls: ['./quotations.page.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, SplitPaneComponent, SplitPaneVerticalComponent, ChatComponent, HistoryComponent, InnerSplitPaneComponent],
})
export class QuotationsPage implements OnInit {
  @ViewChild('deleteQuoteModal') deleteQuoteModal!: TemplateRef<any>;
  @ViewChild('quotationsChat') quotationsChat?: ChatComponent;
  private readonly selectedTemplateStorageKey = 'quotations.selectedTemplateId';
  public quotationsSuggestions: any[] = [
    { icon: 'bi bi-receipt', prompt: 'Draft a quotation for the current project' },
  ];

  public quotationsContext: any = {
    page: 'quotations',
    purpose: 'Prepare, manage and send quotations',
    template: '',
    currency: 'USD',
    outputs: ['Quotation HTML draft', 'Pricing summary', 'Client-facing PDF']
  };

  public readonly rootStore = inject(WorkspaceFacade);
  private readonly docs = inject(DocsFacade);
  private readonly history = inject(HistoryFacade);
  private readonly modal = inject(ModalService);
  private readonly sanitizer = inject(DomSanitizer);
  private lastLoadedProjectId: string | null = null;

  constructor(private readonly quotationsApi: QuotationsService) {
    effect(() => {
      const projectId = this.getActiveProjectId();
      if (projectId === this.lastLoadedProjectId) return;
      this.lastLoadedProjectId = projectId;
      void this.refresh();
    });
  }

  public activeTab: 'templates' | 'active' | 'history' = 'templates';
  public isQuotationsLoading = true;
  public loading = false;
  public error = '';
  public isGenerating = false;
  public isUpdatingQuote = false;
  public isSendingEmail = false;
  public isDeletingQuote = false;
  public isDeletingTemplate = false;
  public isImportingTemplate = false;
  public isTemplatePreviewLoading = false;
  public hasTemplatePreviewError = false;
  public isQuotePreviewLoading = false;
  public hasQuotePreviewError = false;
  public isHistoryPreviewLoading = false;
  public hasHistoryPreviewError = false;
  public templatePreviewNonce = 0;
  public selectedTemplatePreviewUrl: SafeResourceUrl | null = null;
  public selectedActiveQuotePreviewUrl: SafeResourceUrl | null = null;
  public selectedHistoryQuotePreviewUrl: SafeResourceUrl | null = null;
  public importedTemplateInput = '';

  setTab(tab: 'templates' | 'active' | 'history') {
    this.activeTab = tab;
  }

  public templates: QuotationTemplate[] = [];
  public selectedTemplate: QuotationTemplate | null = null;
  public selectedDraftQuoteId: string | null = null;
  public draftEditorName = '';
  public draftEditorPlaceholders: Record<string, string> = {};
  public draftPricingRows: PricingRow[] = [];
  public isTotalAmountManualOverride = false;
  public isSavingDraftEdits = false;
  public generatedDeckUrl = '';
  public generationRecipientEmail = '';

  public generationTitle = '';
  public placeholdersJson = JSON.stringify(
    {
      client_name: 'Acme Corp',
      quote_number: 'Q-1001',
      total_amount: '$12,500',
    },
    null,
    2
  );

  public quotations: QuotationRecord[] = [];
  public selectedActiveQuote: QuotationRecord | null = null;
  public selectedHistoryQuote: QuotationRecord | null = null;
  public selectedQuoteRecipient = '';
  public selectedQuoteSubject = '';
  public selectedQuoteMessage = '';
  public isRefreshingQuote = false;
  public activeProjectSummary: { id: string; name: string } | null = null;
  public projectDefinition: any | null = null;
  public repoContext: any | null = null;
  public pendingDeleteQuote: QuotationRecord | null = null;
  public quoteFabExpanded = false;
  public quoteFabPhase: 'idle' | 'generating' | 'completed' | 'failed' = 'idle';
  public quoteFabError = '';
  public quoteFabDeckUrl = '';
  public chatMessages: ChatMessage[] = [];

  public async ngOnInit(): Promise<void> {
    await this.refresh();
  }

  get activeQuotes(): QuotationRecord[] {
    return this.quotations.filter((q) => q.status === 'ready_to_send' || q.status === 'sent');
  }

  get pendingApprovalQuotes(): QuotationRecord[] {
    return this.quotations.filter((q) => q.status === 'draft' || q.status === 'ready_to_send' || q.status === 'sent');
  }

  get selectedDraftQuote(): QuotationRecord | null {
    const drafts = this.pendingApprovalQuotes;
    if (!drafts.length) return null;
    const selected = String(this.selectedDraftQuoteId ?? '').trim();
    if (!selected) return drafts[0] ?? null;
    return drafts.find((q) => q.id === selected) ?? drafts[0] ?? null;
  }

  get selectedTemplatePendingApprovalQuote(): QuotationRecord | null {
    return this.selectedDraftQuote;
  }

  get selectedTemplatePlaceholderKeys(): string[] {
    return Array.isArray(this.selectedTemplate?.placeholderKeys)
      ? this.selectedTemplate!.placeholderKeys.map((k) => this.normalizePlaceholderKey(k)).filter(Boolean)
      : [];
  }

  get historyQuotes(): QuotationRecord[] {
    return this.quotations.filter((q) => q.status === 'accepted' || q.status === 'rejected' || q.status === 'expired');
  }

  public async refresh(): Promise<void> {
    this.loading = true;
    this.isQuotationsLoading = true;
    this.error = '';
    const selectedTemplateId = this.selectedTemplate?.id ?? this.getPersistedSelectedTemplateId();
    try {
      const projectId = this.getActiveProjectId();
      await this.loadProjectContext(projectId);
      if (!projectId) {
        this.quotations = [];
        this.selectedActiveQuote = null;
        this.selectedHistoryQuote = null;
        this.selectedDraftQuoteId = null;
        this.hydrateDraftEditorFromQuote(null);
        this.updateSelectedTemplatePreview();
        this.updateSelectedQuotePreview();
        this.updateSelectedHistoryPreview();
        return;
      }
      this.templates = [
        {
          id: 'html-template-v1',
          name: 'Professional quotation draft layout',
          placeholderKeys: [
            'client_name',
            'project_name',
            'quote_number',
            'quote_date',
            'valid_until',
            'currency',
            'executive_summary',
            'scope_of_work',
            'deliverables',
            'milestones',
            'timeline',
            'assumptions',
            'exclusions',
            'pricing_lines',
            'total_amount',
            'payment_terms',
            'next_steps',
          ],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      ];
      this.quotations = await this.quotationsApi.listQuotations(projectId);
      const selectedDraftId = String(this.selectedDraftQuoteId ?? '').trim();
      if (selectedDraftId && this.pendingApprovalQuotes.some((q) => q.id === selectedDraftId)) {
        this.selectedDraftQuoteId = selectedDraftId;
      } else {
        this.selectedDraftQuoteId = this.pendingApprovalQuotes[0]?.id ?? null;
      }
      this.hydrateDraftEditorFromQuote(this.selectedDraftQuote);
      this.selectedTemplate = this.templates.find((t) => t.id === selectedTemplateId) ?? this.templates[0] ?? null;
      if (this.selectedTemplate) this.persistSelectedTemplateId(this.selectedTemplate.id);
      this.updateSelectedTemplatePreview();
      this.updateSelectedQuotePreview();
      this.updateSelectedHistoryPreview();
      this.quotationsContext.projectName = this.activeProjectSummary?.name ?? '';
    } finally {
      this.isQuotationsLoading = false;
      this.loading = false;
    }
  }

  public selectTemplate(t: QuotationTemplate): void {
    this.selectedTemplate = t;
    this.persistSelectedTemplateId(t.id);
    this.updateSelectedTemplatePreview();
    this.quotationsContext.template = t.name;
    this.generatedDeckUrl = '';
    this.hydratePayloadFromSelectedTemplate();
  }

  public onTemplatePreviewLoaded(): void {
    this.isTemplatePreviewLoading = false;
    this.hasTemplatePreviewError = false;
  }

  public onTemplatePreviewError(): void {
    this.isTemplatePreviewLoading = false;
    this.hasTemplatePreviewError = true;
  }

  public refreshSelectedTemplatePreview(): void {
    if (!this.selectedDraftQuote) return;
    this.templatePreviewNonce += 1;
    this.updateSelectedTemplatePreview();
  }

  public selectDraftQuote(quote: QuotationRecord): void {
    this.selectedDraftQuoteId = quote.id;
    this.hydrateDraftEditorFromQuote(quote);
    this.updateSelectedTemplatePreview();
  }

  public updateDraftField(key: string, value: string): void {
    const normalized = this.normalizePlaceholderKey(key);
    if (!normalized) return;
    if (normalized === 'total_amount') {
      this.updateTotalAmount(value);
      return;
    }
    this.draftEditorPlaceholders = {
      ...this.draftEditorPlaceholders,
      [normalized]: String(value ?? ''),
    };
    if (normalized === 'pricing_lines') {
      this.syncDraftPricingRowsFromPlaceholders();
      return;
    }
    if (normalized === 'currency') {
      this.syncDraftPricingTotalsFromRows();
    }
  }

  public addPricingRow(): void {
    this.draftPricingRows = [
      ...this.draftPricingRows,
      { label: '', description: '', amount: '' },
    ];
    this.syncPricingLinesPlaceholderFromRows();
  }

  public removePricingRow(index: number): void {
    if (index < 0 || index >= this.draftPricingRows.length) return;
    this.draftPricingRows = this.draftPricingRows.filter((_, rowIndex) => rowIndex !== index);
    this.syncPricingLinesPlaceholderFromRows();
  }

  public updatePricingRow(index: number, field: keyof PricingRow, value: string): void {
    if (index < 0 || index >= this.draftPricingRows.length) return;
    this.draftPricingRows = this.draftPricingRows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [field]: String(value ?? '') } : row
    );
    this.syncPricingLinesPlaceholderFromRows();
  }

  public updateTotalAmount(value: string): void {
    const nextValue = String(value ?? '');
    const calculated = this.draftCalculatedTotalAmount;
    const trimmed = nextValue.trim();
    this.isTotalAmountManualOverride = Boolean(trimmed && (!calculated || trimmed !== calculated));
    this.draftEditorPlaceholders = {
      ...this.draftEditorPlaceholders,
      total_amount: nextValue,
    };
    if (!this.isTotalAmountManualOverride && calculated) {
      this.draftEditorPlaceholders = {
        ...this.draftEditorPlaceholders,
        total_amount: calculated,
      };
    }
  }

  public useCalculatedTotalAmount(): void {
    const calculated = this.draftCalculatedTotalAmount;
    this.isTotalAmountManualOverride = false;
    this.draftEditorPlaceholders = {
      ...this.draftEditorPlaceholders,
      total_amount: calculated,
    };
  }

  get draftCalculatedTotalAmount(): string {
    const currency =
      String(this.draftEditorPlaceholders['currency'] ?? '').trim() ||
      String(this.quotationsContext?.currency ?? 'USD').trim();
    return this.calculatePricingTotal(this.draftPricingRows, currency);
  }

  get hasCalculatedDraftPricingTotal(): boolean {
    return Boolean(this.draftCalculatedTotalAmount);
  }

  get isUsingCalculatedDraftTotal(): boolean {
    const calculated = this.draftCalculatedTotalAmount;
    const current = String(this.draftEditorPlaceholders['total_amount'] ?? '').trim();
    return Boolean(calculated) && !this.isTotalAmountManualOverride && current === calculated;
  }

  public async saveSelectedDraftEdits(): Promise<void> {
    const draft = this.selectedDraftQuote;
    if (!draft || this.isSavingDraftEdits) return;
    this.error = '';
    this.isSavingDraftEdits = true;
    try {
      const nextNameInput = String(this.draftEditorName ?? '').trim();
      if (draft.status === 'draft') {
        const draftId = draft.id;
        const projectId = this.requireActiveProjectId();
        await this.quotationsApi.updateQuotation(projectId, draftId, {
          generatedName: nextNameInput || draft.generatedName,
          placeholders: this.draftEditorPlaceholders,
        });
        await this.refresh();
        this.selectedDraftQuoteId = draftId;
      } else {
        const revisionName =
          nextNameInput && nextNameInput !== draft.generatedName
            ? nextNameInput
            : `${draft.generatedName} - Revision`;
        const projectId = this.requireActiveProjectId();
        const createdDraft = await this.quotationsApi.generateQuotation(projectId, {
          title: revisionName,
          placeholders: this.draftEditorPlaceholders,
          recipientEmail: String(draft.recipientEmail ?? '').trim() || undefined,
        });
        await this.refresh();
        this.selectedDraftQuoteId = createdDraft.id;
      }
      const nextSelected =
        this.pendingApprovalQuotes.find((q) => q.id === this.selectedDraftQuoteId) ?? this.selectedDraftQuote;
      this.hydrateDraftEditorFromQuote(nextSelected);
      this.updateSelectedTemplatePreview();
    } catch (err: any) {
      this.error = String(err?.message ?? 'Failed to save draft edits.');
    } finally {
      this.isSavingDraftEdits = false;
    }
  }

  public onQuotePreviewLoaded(): void {
    this.isQuotePreviewLoading = false;
    this.hasQuotePreviewError = false;
  }

  public onQuotePreviewError(): void {
    this.isQuotePreviewLoading = false;
    this.hasQuotePreviewError = true;
  }

  public onHistoryPreviewLoaded(): void {
    this.isHistoryPreviewLoading = false;
    this.hasHistoryPreviewError = false;
  }

  public onHistoryPreviewError(): void {
    this.isHistoryPreviewLoading = false;
    this.hasHistoryPreviewError = true;
  }

  public async generateQuotation(): Promise<void> {
    let placeholders: Record<string, string> = {};
    try {
      const parsed = JSON.parse(this.placeholdersJson || '{}') as Record<string, unknown>;
      placeholders = {
        ...this.buildProjectScopedSeedPlaceholders(),
        ...this.toPlaceholderMap(parsed),
      };
    } catch {
      this.error = 'Placeholder JSON is invalid.';
      return;
    }
    this.error = '';
    this.isGenerating = true;
    this.quoteFabPhase = 'generating';
    this.quoteFabError = '';
    try {
      const projectId = this.requireActiveProjectId();
      const quote = await this.quotationsApi.generateQuotation(projectId, {
        title: this.generationTitle || undefined,
        placeholders,
        recipientEmail: String(this.generationRecipientEmail ?? '').trim() || undefined,
      });
      this.generatedDeckUrl = quote.previewUrl || quote.webViewLink || '';
      this.quoteFabDeckUrl = this.generatedDeckUrl;
      await this.refresh();
      this.activeTab = 'templates';
      const pending = this.quotations.find((q) => q.id === quote.id) ?? null;
      if (pending) {
        this.selectActiveQuote(pending);
      }
      this.quoteFabPhase = 'completed';
    } catch (err: any) {
      this.error = String(err?.message ?? 'Failed to generate quotation');
      this.quoteFabError = this.error;
      this.quoteFabPhase = 'failed';
    } finally {
      this.isGenerating = false;
    }
  }

  public openExternalQuotation(url: string | undefined): void {
    const link = String(url ?? '').trim();
    if (!link) return;
    window.open(link, '_blank', 'noopener');
  }

  public selectActiveQuote(q: QuotationRecord): void {
    this.selectedActiveQuote = q;
    this.selectedQuoteRecipient = q.recipientEmail ?? q.placeholders['client_email'] ?? '';
    this.selectedQuoteSubject = q.lastSentSubject ?? `Quotation ${q.generatedName}`;
    this.selectedQuoteMessage = q.lastSentBody ?? '';
    this.updateSelectedQuotePreview();
  }

  public selectHistoryQuote(q: QuotationRecord): void {
    this.selectedHistoryQuote = q;
    this.updateSelectedHistoryPreview();
  }

  public async onAiResult(result: AiOrchestratorResult): Promise<void> {
    if (!result || String(result.page ?? '').toLowerCase() !== 'quotations') return;

    const draft = this.buildDraftFromAiResult(result);
    this.generationTitle = draft.title;
    this.placeholdersJson = JSON.stringify(draft.placeholders, null, 2);
    this.generationRecipientEmail = draft.recipientEmail || this.generationRecipientEmail;
    await this.generateQuotation();
  }

  get isQuotationGenerationRunning(): boolean {
    return Boolean(this.isGenerating || this.quotationsChat?.isStreaming);
  }

  get showQuotationFab(): boolean {
    return this.isQuotationGenerationRunning || this.quoteFabPhase === 'completed' || this.quoteFabPhase === 'failed';
  }

  get quoteFabPhaseLabel(): string {
    if (this.quotationsChat?.isStreaming && !this.isGenerating) return 'Analyzing request';
    if (this.isGenerating) return 'Generating document';
    if (this.quoteFabPhase === 'completed') return 'Completed';
    if (this.quoteFabPhase === 'failed') return 'Failed';
    return 'Idle';
  }

  public toggleQuoteFab(): void {
    this.quoteFabExpanded = !this.quoteFabExpanded;
  }

  public async saveSelectedQuoteDetails(): Promise<void> {
    if (!this.selectedActiveQuote) return;
    this.error = '';
    this.isUpdatingQuote = true;
    try {
      const projectId = this.requireActiveProjectId();
      const updated = await this.quotationsApi.updateQuotation(projectId, this.selectedActiveQuote.id, {
        recipientEmail: this.selectedQuoteRecipient || null,
      });
      this.selectedActiveQuote = updated;
      await this.refresh();
    } finally {
      this.isUpdatingQuote = false;
    }
  }

  public async setSelectedQuoteStatus(status: QuotationRecord['status']): Promise<void> {
    if (!this.selectedActiveQuote) return;
    this.error = '';
    this.isUpdatingQuote = true;
    try {
      const projectId = this.requireActiveProjectId();
      const updated = await this.quotationsApi.updateQuotationStatus(projectId, this.selectedActiveQuote.id, status);
      this.selectedActiveQuote = updated;
      await this.refresh();
      if (status === 'accepted' || status === 'rejected' || status === 'expired') {
        this.activeTab = 'history';
      } else if (status === 'ready_to_send') {
        this.activeTab = 'active';
      }
    } finally {
      this.isUpdatingQuote = false;
    }
  }

  public async sendSelectedQuoteEmail(): Promise<void> {
    if (!this.selectedActiveQuote) return;
    const to = String(this.selectedQuoteRecipient ?? '').trim();
    if (!to) {
      this.error = 'Recipient email is required before sending.';
      return;
    }
    this.error = '';
    this.isSendingEmail = true;
    try {
      const projectId = this.requireActiveProjectId();
      const updated = await this.quotationsApi.sendQuotationEmail(projectId, this.selectedActiveQuote.id, {
        to,
        subject: this.selectedQuoteSubject || undefined,
        message: this.selectedQuoteMessage || undefined,
      });
      this.selectedActiveQuote = updated;
      await this.refresh();
    } finally {
      this.isSendingEmail = false;
    }
  }

  public async unarchiveHistoryQuote(q?: QuotationRecord): Promise<void> {
    const target = q ?? this.selectedHistoryQuote;
    if (!target) return;
    this.error = '';
    this.isUpdatingQuote = true;
    try {
      const projectId = this.requireActiveProjectId();
      const updated = await this.quotationsApi.updateQuotationStatus(projectId, target.id, 'ready_to_send');
      await this.refresh();
      const activeQuote = this.activeQuotes.find((item) => item.id === updated.id) ?? updated;
      this.selectActiveQuote(activeQuote);
      this.activeTab = 'active';
    } catch (err: any) {
      this.error = String(err?.message ?? 'Failed to move quotation back to Active.');
    } finally {
      this.isUpdatingQuote = false;
    }
  }

  public openDeleteQuoteModal(q?: QuotationRecord): void {
    const target = q ?? this.selectedActiveQuote;
    if (!target) return;
    this.pendingDeleteQuote = target;
    this.modal.open(this.deleteQuoteModal, { centered: true, backdrop: 'static', keyboard: true });
  }

  public closeDeleteQuoteModal(): void {
    if (this.isDeletingQuote) return;
    this.pendingDeleteQuote = null;
    this.modal.dismiss();
  }

  public async confirmDeleteQuote(): Promise<void> {
    const target = this.pendingDeleteQuote;
    if (!target) return;
    this.error = '';
    this.isDeletingQuote = true;
    try {
      const projectId = this.requireActiveProjectId();
      await this.quotationsApi.deleteQuotation(projectId, target.id);
      this.quotations = this.quotations.filter((q) => q.id !== target.id);

      if (this.selectedDraftQuoteId === target.id) {
        this.selectedDraftQuoteId = this.pendingApprovalQuotes[0]?.id ?? null;
      }
      this.hydrateDraftEditorFromQuote(this.selectedDraftQuote);
      this.updateSelectedTemplatePreview();

      if (this.selectedActiveQuote?.id === target.id) {
        this.selectedActiveQuote = null;
      }
      if (this.selectedHistoryQuote?.id === target.id) {
        this.selectedHistoryQuote = null;
      }
      this.updateSelectedQuotePreview();
      this.updateSelectedHistoryPreview();

      this.modal.close();
      this.pendingDeleteQuote = null;
    } finally {
      this.isDeletingQuote = false;
    }
  }

  public hasDiagnostics(quote: QuotationRecord | null | undefined): boolean {
    if (!quote) return false;
    return (
      (Array.isArray(quote.unmatchedPlaceholders) && quote.unmatchedPlaceholders.length > 0) ||
      (Array.isArray(quote.missingKeys) && quote.missingKeys.length > 0) ||
      (Array.isArray(quote.validationWarnings) && quote.validationWarnings.length > 0)
    );
  }

  public statusLabel(status: QuotationRecord['status'] | null | undefined): string {
    switch (status) {
      case 'ready_to_send':
        return 'Ready to Send';
      case 'sent':
        return 'Sent';
      case 'accepted':
        return 'Accepted';
      case 'rejected':
        return 'Rejected';
      case 'expired':
        return 'Expired';
      case 'draft':
      default:
        return 'Draft';
    }
  }

  public statusPillClass(status: QuotationRecord['status'] | null | undefined): string {
    switch (status) {
      case 'ready_to_send':
        return 'quote-status-ready';
      case 'sent':
        return 'quote-status-sent';
      case 'accepted':
        return 'quote-status-accepted';
      case 'rejected':
        return 'quote-status-rejected';
      case 'expired':
        return 'quote-status-expired';
      case 'draft':
      default:
        return 'quote-status-draft';
    }
  }

  public prepareRegenerationFromDiagnostics(quote: QuotationRecord | null | undefined): void {
    if (!quote) return;
    const seeded = this.buildTemplateAwareSeedPlaceholders(this.selectedTemplate);
    const nextPlaceholders: Record<string, string> = { ...seeded, ...(quote.placeholders ?? {}) };
    for (const key of quote.unmatchedPlaceholders ?? []) {
      const normalized = this.normalizePlaceholderKey(key);
      if (!normalized) continue;
      if (!String(nextPlaceholders[normalized] ?? '').trim()) {
        nextPlaceholders[normalized] = 'TODO: provide value';
      }
    }
    for (const key of quote.missingKeys ?? []) {
      const normalized = this.normalizePlaceholderKey(key);
      if (!normalized) continue;
      nextPlaceholders[normalized] = String(nextPlaceholders[normalized] ?? '').trim() || 'TODO: provide value';
    }
    this.generationTitle = `${quote.generatedName} - Revision`;
    this.generationRecipientEmail = String(quote.recipientEmail ?? this.generationRecipientEmail ?? '').trim();
    this.placeholdersJson = JSON.stringify(nextPlaceholders, null, 2);
    this.activeTab = 'templates';
  }

  public async refreshSelectedQuoteArtifacts(): Promise<void> {
    if (!this.selectedActiveQuote) return;
    this.error = '';
    this.isRefreshingQuote = true;
    try {
      const projectId = this.requireActiveProjectId();
      const refreshed = await this.quotationsApi.refreshQuotation(projectId, this.selectedActiveQuote.id);
      this.selectedActiveQuote = refreshed;
      await this.refresh();
      this.updateSelectedQuotePreview();
    } catch (err: any) {
      this.error = String(err?.message ?? 'Failed to refresh quotation artifacts.');
    } finally {
      this.isRefreshingQuote = false;
    }
  }

  public async approveDraftQuote(quote: QuotationRecord | null | undefined): Promise<void> {
    if (!quote) return;
    this.error = '';
    this.isUpdatingQuote = true;
    try {
      const projectId = this.requireActiveProjectId();
      const updated = await this.quotationsApi.updateQuotationStatus(projectId, quote.id, 'ready_to_send');
      this.selectedActiveQuote = updated;
      await this.refresh();
      this.selectedDraftQuoteId = this.pendingApprovalQuotes[0]?.id ?? null;
      this.hydrateDraftEditorFromQuote(this.selectedDraftQuote);
      this.activeTab = 'active';
    } catch (err: any) {
      this.error = String(err?.message ?? 'Failed to approve quotation.');
    } finally {
      this.isUpdatingQuote = false;
    }
  }

  private buildDraftFromAiResult(result: AiOrchestratorResult): {
    title: string;
    placeholders: Record<string, string>;
    recipientEmail: string;
  } {
    const projectName = String(this.activeProjectSummary?.name ?? '').trim();
    const baseTitle = this.generationTitle || (projectName ? `${projectName} - Quotation Draft` : 'Generated quotation');
    let placeholders: Record<string, string> = this.buildTemplateAwareSeedPlaceholders(this.selectedTemplate);
    let recipientEmail = String(this.generationRecipientEmail ?? '').trim();
    try {
      const parsed = JSON.parse(this.placeholdersJson || '{}') as Record<string, unknown>;
      placeholders = this.mergePlaceholderMaps(placeholders, this.toPlaceholderMap(parsed));
    } catch {
      // keep seeded placeholders
    }

    let title = baseTitle;
    const patches = Array.isArray(result.patches) ? result.patches : [];
    for (const p of patches) {
      const path = String((p as any)?.path ?? '').toLowerCase();
      const value = (p as any)?.value;
      if (path === '/title' || path === '/quotation/title') {
        title = String(value ?? '').trim() || title;
      } else if (path === '/placeholders' || path === '/quotation/placeholders') {
        if (value && typeof value === 'object') {
          placeholders = this.mergePlaceholderMaps(placeholders, this.toPlaceholderMap(value as Record<string, unknown>));
        }
      } else if (path.startsWith('/placeholders/') || path.startsWith('/quotation/placeholders/')) {
        const key = path
          .replace('/quotation/placeholders/', '')
          .replace('/placeholders/', '')
          .trim();
        if (key) {
          const normalized = this.normalizePlaceholderKey(key);
          const nextValue = String(value ?? '').trim();
          if (normalized && nextValue) placeholders[normalized] = nextValue;
        }
      } else if (path === '/recipientemail' || path === '/recipient_email' || path === '/quotation/recipientemail') {
        recipientEmail = String(value ?? '').trim();
      }
    }

    const raw = String(result.message ?? '').trim();
    try {
      const parsed = JSON.parse(raw) as any;
      if (parsed && typeof parsed === 'object') {
        if (parsed.title) title = String(parsed.title);
        if (parsed.placeholders && typeof parsed.placeholders === 'object') {
          placeholders = this.mergePlaceholderMaps(
            placeholders,
            this.toPlaceholderMap(parsed.placeholders as Record<string, unknown>)
          );
        }
        if (parsed.recipientEmail) {
          recipientEmail = String(parsed.recipientEmail ?? '').trim();
        }
      }
    } catch {
      // keep defaults
    }

    if (!recipientEmail) {
      recipientEmail =
        String(placeholders['client_email'] ?? '').trim() ||
        String(placeholders['recipient_email'] ?? '').trim() ||
        String(placeholders['email'] ?? '').trim() ||
        '';
    }

    return { title, placeholders, recipientEmail };
  }

  get quotationsOrchestratorContext(): any {
    const templatePlaceholderSeed = this.buildTemplateAwareSeedPlaceholders(this.selectedTemplate);
    return {
      templates: this.templates,
      selectedTemplate: this.selectedTemplate,
      selectedTemplatePlaceholderKeys: this.selectedTemplatePlaceholderKeys,
      quotations: this.quotations,
      activeProject: this.activeProjectSummary,
      projectDefinition: this.projectDefinition,
      repoContext: this.repoContext,
      placeholderSeed: this.buildProjectScopedSeedPlaceholders(),
      templatePlaceholderSeed,
    };
  }

  private getActiveProjectId(): string | null {
    return this.rootStore.activeProjectId();
  }

  private requireActiveProjectId(): string {
    const projectId = String(this.getActiveProjectId() ?? '').trim();
    if (!projectId) throw new Error('Select a project before managing quotations.');
    return projectId;
  }

  private async loadProjectContext(projectIdInput?: string | null): Promise<void> {
    const projectId = projectIdInput ?? this.getActiveProjectId();
    const projects = this.rootStore.projects() ?? [];
    const selectedId = this.rootStore.selectedProjectId();
    const project = projects.find((p: any) => p?.id === selectedId) ?? projects.find((p: any) => p?.id === projectId) ?? null;
    this.activeProjectSummary = project
      ? { id: String(project.id ?? ''), name: String(project.name ?? '').trim() || 'Current Project' }
      : null;
    if (!projectId) {
      this.chatMessages = [];
      this.projectDefinition = null;
      this.repoContext = null;
      return;
    }

    await this.docs.loadProjectDocs(projectId);
    let chatDoc = this.docs.byProjectAndKey(projectId, 'quotations.chat')();
    const chatKeys = chatDoc?.data && typeof chatDoc.data === 'object' ? Object.keys(chatDoc.data).length : 0;
    if (!chatDoc || chatKeys === 0) {
      chatDoc = await this.docs.loadOne(projectId, 'quotations.chat');
    }
    this.chatMessages = compactChatMessages((chatDoc?.data?.messages ?? []) as ChatMessage[]);

    let defDoc = this.docs.byProjectAndKey(projectId, 'define.definition')();
    const defKeys = defDoc?.data && typeof defDoc.data === 'object' ? Object.keys(defDoc.data).length : 0;
    if (!defDoc || defKeys === 0) {
      defDoc = await this.docs.loadOne(projectId, 'define.definition');
    }
    let reposDoc = this.docs.byProjectAndKey(projectId, 'repos.settings')();
    const reposKeys = reposDoc?.data && typeof reposDoc.data === 'object' ? Object.keys(reposDoc.data).length : 0;
    if (!reposDoc || reposKeys === 0) {
      reposDoc = await this.docs.loadOne(projectId, 'repos.settings');
    }
    this.projectDefinition = defDoc?.data ?? null;
    this.repoContext = reposDoc?.data ?? null;
  }

  public async onChatMessagesChange(messages: ChatMessage[]): Promise<void> {
    const projectId = this.getActiveProjectId();
    if (!projectId) return;
    const nextMessages = compactChatMessages(messages);
    const prev = compactChatMessages(this.chatMessages);
    this.chatMessages = nextMessages;
    if (JSON.stringify(prev) === JSON.stringify(nextMessages)) return;
    try {
      await this.docs.upsert(projectId, 'quotations.chat', { messages: nextMessages });
    } catch (err) {
      // Chat transcript persistence should not block quotation generation/editing flows.
      console.warn('[quotations] Failed to persist chat transcript', err);
    }
  }

  public async onChatMessageCreated(msg: ChatMessage): Promise<void> {
    const projectId = this.getActiveProjectId();
    if (!projectId) return;
    await this.history.add(projectId, 'quotations.chat', msg.text, msg.role as any);
  }

  private toPlaceholderMap(input: Record<string, unknown>): Record<string, string> {
    const next: Record<string, string> = {};
    for (const [rawKey, rawValue] of Object.entries(input ?? {})) {
      const key = this.normalizePlaceholderKey(rawKey);
      if (!key) continue;
      next[key] = String(rawValue ?? '').trim();
    }
    return next;
  }

  private mergePlaceholderMaps(
    base: Record<string, string>,
    incoming: Record<string, string>
  ): Record<string, string> {
    const next = { ...base };
    for (const [key, value] of Object.entries(incoming ?? {})) {
      const normalizedKey = this.normalizePlaceholderKey(key);
      if (!normalizedKey) continue;
      const nextValue = String(value ?? '').trim();
      if (!nextValue && String(next[normalizedKey] ?? '').trim()) {
        continue;
      }
      next[normalizedKey] = nextValue;
    }
    return next;
  }

  private normalizePlaceholderKey(rawKey: string): string {
    return String(rawKey ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_.-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private previewVersionSeed(quote: QuotationRecord | null): string {
    if (!quote) return '';
    const htmlVersion = String(quote.artifacts?.html?.version ?? '').trim();
    const updatedAt = String(quote.updatedAt ?? '').trim();
    return `${htmlVersion}-${updatedAt}`.trim();
  }

  private withCacheBuster(url: string, seed: string): string {
    const base = String(url ?? '').trim();
    if (!base) return '';
    const value = encodeURIComponent(String(seed ?? '').trim() || '0');
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}v=${value}`;
  }

  private buildSlidesPreviewUrl(template: QuotationTemplate | null): string | null {
    if (!template) return null;
    const pendingQuote = this.selectedDraftQuote;
    if (!pendingQuote) return null;
    const baseUrl = String(pendingQuote.previewUrl ?? pendingQuote.webViewLink ?? '').trim();
    if (!baseUrl) return null;
    return this.withCacheBuster(baseUrl, `${this.previewVersionSeed(pendingQuote)}-${this.templatePreviewNonce}`);
  }

  private buildQuotePreviewUrl(quote: QuotationRecord | null): string | null {
    if (!quote) return null;
    const baseUrl = String(quote.previewUrl ?? quote.pdfUrl ?? quote.webViewLink ?? '').trim();
    if (!baseUrl) return null;
    return this.withCacheBuster(baseUrl, this.previewVersionSeed(quote));
  }

  private buildHistoryPreviewUrl(quote: QuotationRecord | null): string | null {
    if (!quote) return null;
    const baseUrl = String(quote.previewUrl ?? quote.pdfUrl ?? quote.webViewLink ?? '').trim();
    if (!baseUrl) return null;
    return this.withCacheBuster(baseUrl, this.previewVersionSeed(quote));
  }

  private updateSelectedTemplatePreview(): void {
    const previewUrl = this.buildSlidesPreviewUrl(this.selectedTemplate);
    this.isTemplatePreviewLoading = Boolean(previewUrl);
    this.hasTemplatePreviewError = false;
    this.selectedTemplatePreviewUrl = previewUrl
      ? this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl)
      : null;
  }

  private hydrateDraftEditorFromQuote(quote: QuotationRecord | null): void {
    if (!quote) {
      this.draftEditorName = '';
      this.draftEditorPlaceholders = {};
      this.draftPricingRows = [];
      this.isTotalAmountManualOverride = false;
      return;
    }
    this.draftEditorName = String(quote.generatedName ?? '').trim();
    this.draftEditorPlaceholders = this.canonicalizeCorePlaceholderKeys(
      this.toPlaceholderMap((quote.placeholders ?? {}) as Record<string, unknown>)
    );
    this.syncDraftPricingRowsFromPlaceholders();
  }

  private canonicalizeCorePlaceholderKeys(placeholders: Record<string, string>): Record<string, string> {
    const source = { ...(placeholders ?? {}) };
    const next = { ...source };
    for (const [canonicalKey, aliases] of Object.entries(CORE_QUOTATION_PLACEHOLDER_ALIASES)) {
      const hasCanonical = Object.prototype.hasOwnProperty.call(source, canonicalKey);
      if (hasCanonical) {
        next[canonicalKey] = String(source[canonicalKey] ?? '').trim();
      } else {
        const candidates = [canonicalKey, ...(aliases ?? [])];
        for (const candidate of candidates) {
          if (!Object.prototype.hasOwnProperty.call(source, candidate)) continue;
          const candidateValue = String(source[candidate] ?? '').trim();
          if (candidateValue) {
            next[canonicalKey] = candidateValue;
            break;
          }
          if (!Object.prototype.hasOwnProperty.call(next, canonicalKey)) {
            next[canonicalKey] = candidateValue;
          }
        }
      }
      for (const alias of aliases ?? []) {
        delete next[alias];
      }
    }
    return next;
  }

  private updateSelectedQuotePreview(): void {
    const previewUrl = this.buildQuotePreviewUrl(this.selectedActiveQuote);
    this.isQuotePreviewLoading = Boolean(previewUrl);
    this.hasQuotePreviewError = false;
    this.selectedActiveQuotePreviewUrl = previewUrl
      ? this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl)
      : null;
  }

  private updateSelectedHistoryPreview(): void {
    const previewUrl = this.buildHistoryPreviewUrl(this.selectedHistoryQuote);
    this.isHistoryPreviewLoading = Boolean(previewUrl);
    this.hasHistoryPreviewError = false;
    this.selectedHistoryQuotePreviewUrl = previewUrl
      ? this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl)
      : null;
  }

  private formatList(values: unknown, maxItems = 5): string {
    if (!Array.isArray(values)) return '';
    return values
      .map((v) => String(v ?? '').trim())
      .filter(Boolean)
      .slice(0, maxItems)
      .join(', ');
  }

  private parseMoneyAmount(value: string): number | null {
    const cleaned = String(value ?? '')
      .replace(/[^0-9.-]/g, '')
      .trim();
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private formatCurrencyAmount(amount: number, currency: string): string {
    if (!Number.isFinite(amount)) return '';
    const currencyCode = String(currency ?? '').trim().toUpperCase();
    try {
      if (/^[A-Z]{3}$/.test(currencyCode)) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currencyCode,
          maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
        }).format(amount);
      }
    } catch {
      // Fall back to a plain numeric formatter if the currency code is invalid.
    }
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
  }

  private parsePricingRows(value: string): PricingRow[] {
    return String(value ?? '')
      .replace(/\r/g, '\n')
      .split(/\n|;/)
      .map((row) => row.trim())
      .filter(Boolean)
      .map((row) => {
        const [label = '', description = '', amount = ''] = row.split('|').map((part) => String(part ?? '').trim());
        return { label, description, amount };
      })
      .filter((row) => row.label || row.description || row.amount);
  }

  private serializePricingRows(rows: PricingRow[]): string {
    return rows
      .map((row) => ({
        label: String(row.label ?? '').trim(),
        description: String(row.description ?? '').trim(),
        amount: String(row.amount ?? '').trim(),
      }))
      .filter((row) => row.label || row.description || row.amount)
      .map((row) => [row.label, row.description, row.amount].join('|'))
      .join('\n');
  }

  private calculatePricingTotal(rows: PricingRow[], currency: string): string {
    const amounts = rows
      .map((row) => this.parseMoneyAmount(row.amount))
      .filter((amount): amount is number => amount !== null);
    if (!amounts.length) return '';
    const total = amounts.reduce((sum, amount) => sum + amount, 0);
    return this.formatCurrencyAmount(total, currency);
  }

  private isDefaultUneditedTotalAmount(value: string): boolean {
    const raw = String(value ?? '').trim();
    return !raw || raw === '$0.00' || raw === '0' || raw === '0.00';
  }

  private syncDraftPricingRowsFromPlaceholders(): void {
    const pricingLines = String(this.draftEditorPlaceholders['pricing_lines'] ?? '');
    this.draftPricingRows = this.parsePricingRows(pricingLines);
    const currentTotal = String(this.draftEditorPlaceholders['total_amount'] ?? '').trim();
    const calculated = this.draftCalculatedTotalAmount;
    this.isTotalAmountManualOverride = calculated
      ? Boolean(currentTotal && currentTotal !== calculated)
      : !this.isDefaultUneditedTotalAmount(currentTotal);
    if (!this.isTotalAmountManualOverride && calculated && currentTotal !== calculated) {
      this.draftEditorPlaceholders = {
        ...this.draftEditorPlaceholders,
        total_amount: calculated,
      };
    }
  }

  private syncPricingLinesPlaceholderFromRows(): void {
    this.draftEditorPlaceholders = {
      ...this.draftEditorPlaceholders,
      pricing_lines: this.serializePricingRows(this.draftPricingRows),
    };
    this.syncDraftPricingTotalsFromRows();
  }

  private syncDraftPricingTotalsFromRows(): void {
    if (this.isTotalAmountManualOverride) return;
    this.draftEditorPlaceholders = {
      ...this.draftEditorPlaceholders,
      total_amount: this.draftCalculatedTotalAmount,
    };
  }

  private formatIsoDate(value: Date): string {
    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private buildExecutiveSummary(definition: any, projectName: string): string {
    const summaryParts = [
      String(definition?.description ?? '').trim(),
      this.formatList(definition?.goals, 3),
    ].filter(Boolean);
    const audience = String(definition?.targetUsers ?? '').trim();
    const timeline = String(definition?.timeline ?? '').trim();
    const lead = projectName
      ? `${projectName} is proposed as a focused delivery engagement`
      : 'This quotation outlines a focused delivery engagement';
    const detail = summaryParts.join(' ');
    const audienceClause = audience ? ` for ${audience}` : '';
    const timelineClause = timeline ? ` over an expected timeline of ${timeline}` : '';
    return `${lead}${audienceClause}${timelineClause}. ${detail}`.trim();
  }

  private buildDefaultNextSteps(): string {
    return 'Review and approve this quotation, confirm the preferred kickoff window, and we will finalize the delivery plan and start the engagement.';
  }

  private buildProjectScopedSeedPlaceholders(): Record<string, string> {
    const definition = this.projectDefinition && typeof this.projectDefinition === 'object' ? this.projectDefinition : {};
    const now = new Date();
    const quoteDate = this.formatIsoDate(now);
    const validUntilDate = new Date(now);
    validUntilDate.setDate(validUntilDate.getDate() + 14);
    const validUntil = this.formatIsoDate(validUntilDate);
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const quoteNumber = `Q-${yyyy}${mm}${dd}-${Math.floor(Math.random() * 900 + 100)}`;
    const projectName = String((definition as any)?.name ?? this.activeProjectSummary?.name ?? 'Project').trim();

    const seed: Record<string, string> = {
      quote_number: quoteNumber,
      quote_date: quoteDate,
      valid_until: validUntil,
      project_name: projectName,
      project_description: String((definition as any)?.description ?? '').trim(),
      client_name:
        String(((definition as any)?.stakeholders?.[0] ?? '')).trim() ||
        String(this.activeProjectSummary?.name ?? 'Client').trim(),
      domain: String((definition as any)?.domain ?? '').trim(),
      target_users: String((definition as any)?.targetUsers ?? '').trim(),
      timeline: String((definition as any)?.timeline ?? '').trim(),
      budget: String((definition as any)?.budget ?? '').trim(),
      team_size: String((definition as any)?.teamSize ?? '').trim(),
      executive_summary: this.buildExecutiveSummary(definition, projectName),
      scope_of_work: this.formatList((definition as any)?.inScope, 8) || this.formatList((definition as any)?.features, 8),
      key_features: this.formatList((definition as any)?.features, 8),
      goals_summary: this.formatList((definition as any)?.goals, 6),
      milestones: this.formatList((definition as any)?.milestones, 6),
      assumptions: this.formatList((definition as any)?.assumptions, 5),
      exclusions: this.formatList((definition as any)?.outOfScope, 5),
      integrations: this.formatList((definition as any)?.integrations, 6),
      deliverables: this.formatList((definition as any)?.keyScreens, 8),
      pricing_lines: '',
      payment_terms: '50% upfront, 50% on delivery',
      total_amount: '$0.00',
      currency: String(this.quotationsContext?.currency ?? 'USD'),
      next_steps: this.buildDefaultNextSteps(),
    };

    const cleanedEntries = Object.entries(seed)
      .map(([k, v]) => [this.normalizePlaceholderKey(k), String(v ?? '').trim()])
      .filter(([k, v]) => Boolean(k && v));
    return Object.fromEntries(cleanedEntries);
  }

  private buildTemplateAwareSeedPlaceholders(template: QuotationTemplate | null): Record<string, string> {
    const base = this.buildProjectScopedSeedPlaceholders();
    const keys = Array.isArray(template?.placeholderKeys)
      ? template!.placeholderKeys.map((k) => this.normalizePlaceholderKey(k)).filter(Boolean)
      : [];
    if (keys.length === 0) return base;
    const next: Record<string, string> = {};
    for (const key of keys) {
      next[key] = String(base[key] ?? '').trim();
    }
    return this.mergePlaceholderMaps(next, base);
  }

  private hydratePayloadFromSelectedTemplate(): void {
    const base = this.buildTemplateAwareSeedPlaceholders(this.selectedTemplate);
    try {
      const parsed = JSON.parse(this.placeholdersJson || '{}') as Record<string, unknown>;
      const merged = this.mergePlaceholderMaps(base, this.toPlaceholderMap(parsed));
      this.placeholdersJson = JSON.stringify(merged, null, 2);
      return;
    } catch {
      // fall through to seed reset
    }
    this.placeholdersJson = JSON.stringify(base, null, 2);
  }

  private getPersistedSelectedTemplateId(): string | null {
    try {
      return String(window.localStorage.getItem(this.selectedTemplateStorageKey) ?? '').trim() || null;
    } catch {
      return null;
    }
  }

  private persistSelectedTemplateId(id: string): void {
    const value = String(id ?? '').trim();
    if (!value) return;
    try {
      window.localStorage.setItem(this.selectedTemplateStorageKey, value);
    } catch {
      // ignore persistence errors
    }
  }

  private clearPersistedSelectedTemplateId(): void {
    try {
      window.localStorage.removeItem(this.selectedTemplateStorageKey);
    } catch {
      // ignore persistence errors
    }
  }
}